import React, { useState, useEffect } from 'react';
import { 
  X, 
  Hash, 
  User, 
  Briefcase, 
  Building2, 
  Calendar, 
  Banknote, 
  Activity,
  Mail,
  Phone,
  Save,
  Loader2
} from 'lucide-react';
import styles from './EmployeeDrawer.module.css';
import api from '../../api/axios';
import toast from 'react-hot-toast';
import CustomDatePicker from '../../components/Common/DatePicker/CustomDatePicker';
import CustomSelect from '../../components/Common/Select/CustomSelect';

const EmployeeDrawer = ({ isOpen, onClose, onSave, employee = null }) => {
  const [formData, setFormData] = useState({
    employee_code: '',
    employee_name: '',
    email: '',
    phone_number: '',
    designation_id: '',
    department_id: '',
    joining_date: '',
    salary: '',
    status: 1, // 1: Active, 0: Inactive/On Leave/Probating - Wait, the image shows 3 statuses. 
    // I might need to adjust the backend or handle mapping if status is just boolean.
    // The current migration shows status is boolean (0/1). 
    // I'll use a numeric mapping for these 3 states if possible, or just stay with boolean for now.
    // Let's assume: Active = 1, others = 0 or different values if I update DB later.
  });

  const [departments, setDepartments] = useState([]);
  const [designations, setDesignations] = useState([]);
  const [loading, setLoading] = useState(false);
  const [isFetching, setIsFetching] = useState(false);

  useEffect(() => {
    const fetchEmployeeDetail = async () => {
      if (employee && isOpen) {
        setIsFetching(true);
        try {
          // Pre-populate with existing data from props while fetching
          setFormData({
            employee_code: employee.employee_code || '',
            employee_name: employee.employee_name || '',
            email: employee.email || '',
            phone_number: employee.phone_number || '',
            designation_id: employee.designation_id || '',
            department_id: employee.department_id || '',
            joining_date: employee.joining_date ? employee.joining_date.split('T')[0] : '',
            salary: employee.salary || '',
            status: employee.status || 1,
          });

          const response = await api.get(`/employees/${employee.id}`);
          if (response.data.success) {
            const data = response.data.data;
            setFormData({
              employee_code: data.employee_code || '',
              employee_name: data.employee_name || '',
              email: data.email || '',
              phone_number: data.phone_number || '',
              designation_id: data.designation_id || '',
              department_id: data.department_id || '',
              joining_date: data.joining_date ? data.joining_date.split(' ')[0] : '',
              salary: data.salary || '',
              status: data.status !== undefined ? data.status : 1,
            });
          }
        } catch (error) {
          console.error('Error fetching employee details:', error);
          toast.error('Failed to fetch latest employee details');
        } finally {
          setIsFetching(false);
        }
      } else if (!employee) {
        setFormData({
          employee_code: '',
          employee_name: '',
          email: '',
          phone_number: '',
          designation_id: '',
          department_id: '',
          joining_date: '',
          salary: '',
          status: 1,
        });
        fetchNextCode();
        setIsFetching(false);
      }
    };

    if (isOpen) {
      fetchDropdownData();
      fetchEmployeeDetail();
    }
  }, [isOpen, employee]);

  const fetchNextCode = async () => {
    try {
      const response = await api.get('/employees/next-code');
      if (response.data.success) {
        setFormData(prev => ({ ...prev, employee_code: response.data.data }));
      }
    } catch (error) {
      console.error('Error fetching next employee code:', error);
    }
  };

  const fetchDropdownData = async () => {
    try {
      const [deptRes, desigRes] = await Promise.all([
        api.get('/departments'),
        api.get('/designations')
      ]);
      setDepartments(deptRes.data.data.data || deptRes.data.data || []);
      setDesignations(desigRes.data.data.data || desigRes.data.data || []);
    } catch (error) {
      console.error('Error fetching dropdown data:', error);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleStatusChange = (status) => {
    setFormData(prev => ({ ...prev, status }));
  };

  const handleSelectChange = (selectedOption, name) => {
    setFormData(prev => ({ 
      ...prev, 
      [name]: selectedOption ? selectedOption.value : '' 
    }));
  };

  const handleDateChange = (date) => {
    setFormData(prev => ({ 
      ...prev, 
      joining_date: date ? date.toLocaleDateString('en-CA') : '' 
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      let response;
      if (employee) {
        response = await api.put(`/employees/${employee.id}`, formData);
      } else {
        response = await api.post('/employees', formData);
      }

      if (response.data.success) {
        toast.success(employee ? 'Employee updated successfully' : 'Employee added successfully');
        onSave();
        onClose();
      }
    } catch (error) {
      console.error('Error saving employee:', error);
      const message = error.response?.data?.message || 'Failed to save employee';
      toast.error(message);
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className={styles['drawer-backdrop']} onClick={onClose}>
      <div className={styles['drawer-container']} onClick={e => e.stopPropagation()}>
        <div className={styles['drawer-header']}>
          <div className={styles['header-title-wrapper']}>
            <h2>{employee ? 'Edit Employee' : 'Add Employee'}</h2>
            <p>Fill in the staff registration details</p>
          </div>
          <button className={styles['close-btn']} onClick={onClose}>
            <X size={24} />
          </button>
        </div>

        <form onSubmit={handleSubmit} className={styles['drawer-form']}>
          <div className={styles['form-scroll-area']}>
            {isFetching ? (
              <div className={styles['loading-container']}>
                <Loader2 size={40} className={styles['spinner']} />
                <span>Fetching employee details...</span>
              </div>
            ) : (
              <>
                {/* Employee Code */}
                <div className={styles['form-group']}>
                  <label><Hash size={16} /> Employee Code <span className={styles['required-star']}>*</span></label>
                  <input 
                    type="text" 
                    name="employee_code"
                    value={formData.employee_code}
                    onChange={handleChange}
                    placeholder="Generating..."
                    required
                    readOnly
                  />
                </div>

                {/* Full Name */}
                <div className={styles['form-group']}>
                  <label><User size={16} /> Full Name <span className={styles['required-star']}>*</span></label>
                  <input 
                    type="text" 
                    name="employee_name"
                    value={formData.employee_name}
                    onChange={handleChange}
                    placeholder="Enter employee name"
                    required
                  />
                </div>

                {/* Email Address */}
                <div className={styles['form-group']}>
                  <label><Mail size={16} /> Email Address</label>
                  <input 
                    type="email" 
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    placeholder="employee@example.com"
                  />
                </div>

                {/* Phone Number */}
                <div className={styles['form-group']}>
                  <label><Phone size={16} /> Phone Number</label>
                  <input 
                    type="text" 
                    name="phone_number"
                    value={formData.phone_number}
                    onChange={handleChange}
                    placeholder="+91 XXXXX XXXXX"
                  />
                </div>

                {/* Designation */}
                <div className={styles['form-group']}>
                  <label><Briefcase size={16} /> Designation <span className={styles['required-star']}>*</span></label>
                  <CustomSelect 
                    name="designation_id"
                    value={designations.find(des => des.id === formData.designation_id) ? {
                      value: formData.designation_id,
                      label: designations.find(des => des.id === formData.designation_id).designation_name
                    } : null}
                    onChange={(option) => handleSelectChange(option, 'designation_id')}
                    options={designations.map(des => ({
                      value: des.id,
                      label: des.designation_name
                    }))}
                    placeholder="Select Designation"
                    required
                  />
                </div>

                {/* Department */}
                <div className={styles['form-group']}>
                  <label><Building2 size={16} /> Department <span className={styles['required-star']}>*</span></label>
                  <CustomSelect 
                    name="department_id"
                    value={departments.find(dept => dept.id === formData.department_id) ? {
                      value: formData.department_id,
                      label: departments.find(dept => dept.id === formData.department_id).departement_name
                    } : null}
                    onChange={(option) => handleSelectChange(option, 'department_id')}
                    options={departments.map(dept => ({
                      value: dept.id,
                      label: dept.departement_name
                    }))}
                    placeholder="Select Department"
                    required
                  />
                </div>

                {/* Joining Date */}
                <div className={styles['form-group']}>
                  <label><Calendar size={16} /> Joining Date</label>
                  <CustomDatePicker
                    selected={formData.joining_date}
                    onChange={handleDateChange}
                    placeholderText="Select joining date"
                  />
                </div>

                {/* Monthly Salary */}
                <div className={styles['form-group']}>
                  <label><Banknote size={16} /> Monthly Salary</label>
                  <div className={styles['salary-input-wrapper']}>
                    <span className={styles['currency']}>₹</span>
                    <input 
                      type="number" 
                      name="salary"
                      value={formData.salary}
                      onChange={handleChange}
                      placeholder="0.00"
                    />
                  </div>
                </div>

                {/* Employment Status */}
                <div className={styles['form-group']}>
                  <label><Activity size={16} /> Employment Status</label>
                  <div className={styles['status-toggle-wrapper']}>
                    <button 
                      type="button"
                      className={`${styles['status-btn']} ${formData.status === 1 ? styles['active'] : ''}`}
                      onClick={() => handleStatusChange(1)}
                    >
                      Active
                    </button>
                    <button 
                      type="button"
                      className={`${styles['status-btn']} ${formData.status === 0 ? styles['inactive'] : ''}`}
                      onClick={() => handleStatusChange(0)}
                    >
                      Inactive
                    </button>
                  </div>
                </div>
              </>
            )}
          </div>

          <div className={styles['drawer-footer']}>
            <button type="button" className={styles['cancel-btn']} onClick={onClose}>
              Cancel
            </button>
            <button type="submit" className={styles['save-btn']} disabled={loading}>
              {loading ? <Loader2 size={18} className={styles['spinner']} /> : <Save size={18} />}
              <span>{employee ? 'Update Employee' : 'Save Employee'}</span>
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default EmployeeDrawer;
